import { validateCustomerDetails } from './customerDetails';
import { collectQuoteUploadFiles, setQuoteUploadFiles } from './quoteUploads';

export const PRODUCT_TYPES_BY_PATH = {
  '/badges': 'Badges',
  '/bookmarks': 'Bookmarks',
  '/brochures': 'Brochures',
  '/business-cards': 'Business Cards',
  '/calendars': 'Calendars',
  '/catalogues': 'Catalogues',
  '/certificates': 'Certificates',
  '/coated-paper-bags': 'Coated Paper Bags',
  '/envelopes': 'Envelopes',
  '/flyer-product': 'Flyers',
  '/folders': 'Folders',
  '/id-cards': 'ID Cards',
  '/invitations': 'Invitations',
  '/kraft-paper-bags': 'Kraft Paper Bags',
  '/leaflets': 'Leaflets',
  '/letterheads': 'Letterheads',
  '/menus': 'Menus',
  '/notebooks': 'Custom Notebooks',
  '/postcards': 'Postcards',
  '/prescription-pads': 'Prescription Pads',
  '/self-carbon-notebooks': 'Self Carbon Notebooks',
};

function cleanText(value) {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
}

function labelText(group) {
  const label = group.querySelector('label');
  return cleanText(label?.getAttribute('data-en')) || cleanText(label?.textContent);
}

const OPTIONAL_SPECIFICATION_LABELS = new Set([
  'Additional Notes',
  'Notes',
  'Packaging',
  'Upload Design Files',
]);

function isOtherValue(value) {
  return cleanText(value).toLowerCase() === 'other';
}

function customPaperField(group) {
  return group.querySelector('.custom-paper-input');
}

function ensureCustomPaperField(group) {
  const select = group.querySelector('select');
  const isOther = isOtherValue(select?.value);
  let field = group.querySelector('.custom-paper-field');

  if (!isOther) {
    field?.remove();
    return null;
  }

  if (!field) {
    field = document.createElement('div');
    field.className = 'custom-paper-field';

    const label = document.createElement('label');
    label.setAttribute('data-en', 'Other Paper Type & Weight');
    label.setAttribute('data-ar', 'نوع ووزن الورق الآخر');
    label.textContent = document.documentElement.lang === 'ar'
      ? 'نوع ووزن الورق الآخر'
      : 'Other Paper Type & Weight';

    const input = document.createElement('input');
    input.className = 'custom-paper-input';
    input.type = 'text';
    input.placeholder = document.documentElement.lang === 'ar'
      ? 'اكتب نوع ووزن الورق'
      : 'Enter paper type and weight';

    field.append(label, input);
    group.appendChild(field);
  }

  return field;
}

export function syncCustomPaperFields(configuration) {
  configuration?.querySelectorAll('.config-group').forEach((group) => {
    const label = labelText(group);
    if (label === 'Paper Type' || label === 'Paper Type & Weight') ensureCustomPaperField(group);
  });
}

function removeConfigurationError(group) {
  group.classList.remove('config-group-invalid');
  group.querySelector('.config-group-error')?.remove();
}

function showConfigurationError(group) {
  removeConfigurationError(group);
  group.classList.add('config-group-invalid');
  const error = document.createElement('p');
  error.className = 'config-group-error';
  error.textContent = document.documentElement.lang === 'ar'
    ? 'يرجى اختيار أو إدخال هذا الخيار.'
    : 'Please select or enter this option.';
  group.appendChild(error);
}

function groupHasValue(group) {
  const selectedOption = group.querySelector(
    '.size-option.active, .side-option.active, .color-option.active, .folding-option.active, .folding-option-btn.active',
  );
  if (selectedOption) {
    const isCustomSize = selectedOption.matches('.size-option[data-size="Other"], .size-option[data-size="Custom"]');
    return !isCustomSize || Boolean(cleanText(group.querySelector('.custom-size-input')?.value));
  }

  if (group.querySelector('input[type="radio"], input[type="checkbox"]')) {
    const hasChecked = Boolean(group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked'));
    if (hasChecked) {
      const extraInput = group.querySelector('input[type="text"]:not(.custom-paper-input):not(.custom-size-input), textarea');
      if (extraInput && extraInput.offsetParent !== null) {
        return Boolean(cleanText(extraInput.value));
      }
      
      const extraFileInput = group.querySelector('input[type="file"]');
      if (extraFileInput && extraFileInput.offsetParent !== null) {
        return extraFileInput.files?.length > 0;
      }
    }
    return hasChecked;
  }

  const select = group.querySelector('select');
  if (select) {
    if (select.selectedIndex < 0 || !cleanText(select.value)) return false;
    return !isOtherValue(select.value) || Boolean(cleanText(customPaperField(group)?.value));
  }

  const control = group.querySelector('input:not([type="file"]), textarea');
  return Boolean(cleanText(control?.value));
}

export function validateQuoteConfiguration(configuration, { showErrors = true } = {}) {
  if (!configuration) return { firstInvalid: null, isValid: false };

  syncCustomPaperFields(configuration);
  let firstInvalid = null;

  configuration.querySelectorAll('.config-group').forEach((group) => {
    const label = labelText(group);
    const optional = OPTIONAL_SPECIFICATION_LABELS.has(label)
      || Boolean(group.querySelector('.quote-notes-input, .upload-area'));
    const valid = optional || groupHasValue(group);

    if (valid) {
      removeConfigurationError(group);
    } else {
      firstInvalid ||= group;
      if (showErrors) showConfigurationError(group);
    }
  });

  return { firstInvalid, isValid: !firstInvalid };
}

export function initializeEmptyQuoteConfiguration(configuration) {
  if (!configuration) return;

  configuration.querySelectorAll(
    '.size-option.active, .side-option.active, .color-option.active, .folding-option.active, .folding-option-btn.active',
  ).forEach((option) => option.classList.remove('active'));

  configuration.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((control) => {
    control.checked = false;
  });

  configuration.querySelectorAll('select').forEach((select) => {
    select.selectedIndex = -1;
  });

  const quantity = configuration.querySelector('#quantity');
  if (quantity) quantity.value = '';

  configuration.querySelectorAll('.summary-value').forEach((value) => {
    value.textContent = '—';
  });

  syncCustomPaperFields(configuration);
}

function selectedText(control) {
  if (control.tagName === 'SELECT') {
    return cleanText(control.options[control.selectedIndex]?.getAttribute('data-en'))
      || cleanText(control.options[control.selectedIndex]?.textContent)
      || cleanText(control.value);
  }
  return cleanText(control.value);
}

function checkedText(control) {
  const label = control.closest('label');
  const translatedText = label?.querySelector('[data-en]')?.getAttribute('data-en');
  return cleanText(translatedText) || cleanText(label?.textContent) || cleanText(control.value);
}

function selectedOptionText(option) {
  if (option.matches('.size-option')) {
    return cleanText(option.dataset.size) || cleanText(option.textContent);
  }

  return cleanText(option.getAttribute('data-en'))
    || cleanText(option.querySelector('[data-en]')?.getAttribute('data-en'))
    || cleanText(option.textContent)
    || cleanText(option.dataset.sides)
    || cleanText(option.dataset.color)
    || cleanText(option.dataset.fold);
}

function normalizedOptionValues(option) {
  return [
    option.dataset.size,
    option.dataset.sides,
    option.dataset.color,
    option.dataset.fold,
    option.getAttribute('data-en'),
    option.querySelector('[data-en]')?.getAttribute('data-en'),
    option.textContent,
  ].map(cleanText).filter(Boolean);
}

function valuesMatch(first, second) {
  return cleanText(first).toLowerCase() === cleanText(second).toLowerCase();
}

function restoreGroupValue(group, value) {
  const values = Array.isArray(value) ? value : [value];
  const options = Array.from(group.querySelectorAll(
    '.size-option, .side-option, .color-option, .folding-option, .folding-option-btn',
  ));

  if (options.length) {
    const selected = options.find((option) => (
      values.some((savedValue) => normalizedOptionValues(option).some((optionValue) => valuesMatch(optionValue, savedValue)))
    ));
    const customInput = group.querySelector('.custom-size-input');
    const customOption = options.find((option) => option.matches('[data-size="Other"], [data-size="Custom"]'));
    const activeOption = selected || (customInput && customOption ? customOption : null);

    if (activeOption) {
      options.forEach((option) => option.classList.toggle('active', option === activeOption));
      if (!selected && customInput) customInput.value = cleanText(value);
    }
    return;
  }

  const checks = Array.from(group.querySelectorAll('input[type="radio"], input[type="checkbox"]'));
  if (checks.length) {
    checks.forEach((control) => {
      control.checked = values.some((savedValue) => valuesMatch(checkedText(control), savedValue));
      control.dispatchEvent(new Event('change', { bubbles: true }));
    });
    return;
  }

  const select = group.querySelector('select');
  if (select) {
    const matchingOption = Array.from(select.options).find((option) => (
      valuesMatch(option.getAttribute('data-en'), value)
      || valuesMatch(option.textContent, value)
      || valuesMatch(option.value, value)
    ));
    if (matchingOption) {
      select.value = matchingOption.value;
    } else {
      const otherOption = Array.from(select.options).find((option) => isOtherValue(option.value));
      if (otherOption) {
        select.value = otherOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        const paperInput = customPaperField(group);
        if (paperInput) paperInput.value = cleanText(value);
      }
    }
    return;
  }

  const control = group.querySelector('input:not([type="file"]), textarea');
  if (control) control.value = Array.isArray(value) ? value.join(', ') : value;
}

export function restoreQuoteConfiguration(configuration, item) {
  if (!configuration || !item) return;

  const quantity = configuration.querySelector('#quantity');
  if (quantity && item.quantity) quantity.value = item.quantity;

  const notes = configuration.querySelector('.quote-notes-input');
  if (notes) notes.value = item.notes || '';

  const specifications = item.specifications && typeof item.specifications === 'object'
    ? item.specifications
    : {};

  configuration.querySelectorAll('.config-group').forEach((group) => {
    const label = labelText(group);
    if (!label || label === 'Quantity' || group.querySelector('.quote-notes-input')) return;
    if (Object.prototype.hasOwnProperty.call(specifications, label)) {
      restoreGroupValue(group, specifications[label]);
    }
  });

  configuration.dispatchEvent(new Event('change', { bubbles: true }));
}

function canonicalProductType(pathname, fallback = '') {
  return PRODUCT_TYPES_BY_PATH[pathname] || cleanText(fallback) || 'Unknown Product';
}

export function normalizeQuoteQuantity(value) {
  const quantity = Number(value);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : null;
}

export function hasValidQuoteQuantity(item) {
  return normalizeQuoteQuantity(item?.quantity) !== null;
}

function collectSpecifications(configuration) {
  if (!configuration) return {};

  const specifications = {};

  configuration.querySelectorAll('.config-group').forEach((group) => {
    const label = labelText(group);
    if (!label) return;
    if (group.querySelector('.quote-notes-input')) return;

    const selectedOption = group.querySelector('.size-option.active, .side-option.active, .color-option.active, .folding-option.active, .folding-option-btn.active');
    if (selectedOption) {
      const customSize = group.querySelector('.custom-size-input')?.value.trim();
      const isCustomSize = selectedOption.matches('.size-option[data-size="Other"], .size-option[data-size="Custom"]');

      specifications[label] = isCustomSize && customSize
        ? cleanText(customSize)
        : selectedOptionText(selectedOption);
      return;
    }

    const checked = Array.from(group.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'));
    if (checked.length) {
      const values = checked.map(checkedText);
      let finalValue = values.length === 1 ? values[0] : values;
      
      const extraInput = group.querySelector('input[type="text"]:not(.custom-paper-input):not(.custom-size-input), textarea');
      if (extraInput && extraInput.offsetParent !== null && extraInput.value.trim()) {
        finalValue = `${Array.isArray(finalValue) ? finalValue.join(', ') : finalValue} - ${cleanText(extraInput.value)}`;
      }

      const extraFileInput = group.querySelector('input[type="file"]');
      if (extraFileInput && extraFileInput.offsetParent !== null && extraFileInput.files?.length) {
        const fileNames = Array.from(extraFileInput.files, (f) => f.name).join(', ');
        finalValue = `${Array.isArray(finalValue) ? finalValue.join(', ') : finalValue} (Files: ${fileNames})`;
      }
      
      specifications[label] = finalValue;
      return;
    }

    const select = group.querySelector('select');
    if (select?.selectedIndex >= 0 && select.value) {
      const customPaper = customPaperField(group)?.value;
      specifications[label] = isOtherValue(select.value) && cleanText(customPaper)
        ? cleanText(customPaper)
        : selectedText(select);
      return;
    }

    const control = group.querySelector('input:not([type="file"]):not([type="radio"]):not([type="checkbox"]), textarea');
    if (control?.value) specifications[label] = selectedText(control);

    const files = group.querySelector('input[type="file"]')?.files;
    if (files?.length) specifications[label] = Array.from(files, (file) => file.name);
  });

  return specifications;
}

export function createQuoteItem(productType, pathname) {
  const configuration = document.querySelector('.configuration-section');
  const specifications = collectSpecifications(configuration);
  const quantity = normalizeQuoteQuantity(configuration?.querySelector('#quantity')?.value);
  const notes = cleanText(configuration?.querySelector('.quote-notes-input')?.value);
  const id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  delete specifications.Quantity;

  const summary = Object.entries(specifications)
    .slice(0, 3)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(' | ');

  const item = {
    id,
    notes,
    pathname,
    productType: canonicalProductType(pathname, productType),
    quantity,
    specifications: { ...specifications, source_path: pathname },
    summary,
  };

  setQuoteUploadFiles(id, collectQuoteUploadFiles(configuration));
  return item;
}

export function createQuoteRequestRows(items, contact) {
  const contactValidation = validateCustomerDetails(contact);
  if (!contactValidation.isValid) {
    throw new Error('Valid customer name, email, and phone are required.');
  }
  if (items.some((item) => !hasValidQuoteQuantity(item))) {
    throw new Error('A valid quantity is required.');
  }

  return items.map((item) => {
    const pathname = item.pathname || item.specifications?.source_path || '';
    const specifications = { ...(item.specifications || {}) };
    delete specifications.Quantity;
    specifications.source_path = pathname;

    return {
      customer_name: contactValidation.normalized.name,
      customer_email: contactValidation.normalized.email,
      customer_phone: contactValidation.normalized.phone,
      product_type: canonicalProductType(pathname, item.productType),
      quantity: normalizeQuoteQuantity(item.quantity),
      specifications,
      notes: cleanText(item.notes) || null,
      status: 'pending',
    };
  });
}
